/* eslint-disable no-restricted-globals */
import './_search-filter.scss';
import './_search-filter.dark-mode.scss';
import React, { useState, useEffect, useRef, createRef, useMemo, useCallback } from 'react';
import { Dropdown, ControlLabel } from 'react-bootstrap';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import Menu from './components/Menu';
import Toggle from './components/Toggle';

import useKeyboard from './hooks/useKeyboard';

const SearchFilter = ({ options, className, label, toggleText, showOptionId, preselectFirst, grouped, listMaxHeight, onSelect }) => {
  const inputRef = useRef(null);

  const [inputValue, setInputValue] = useState('');
  const [selected, setSelected] = useState(null);
  const [toggle, setToggle] = useState('');
  const [open, setOpen] = useState(false);

  const onToggleClose = () => setOpen(!open);

  const handleFocus = () => inputRef?.current?.focus();

  const filteredOptions = useMemo(() => {
    if (grouped) {
      return options
        ?.reduce((acc, item) => {
          const filteredServices = item?.options?.filter((item) => {
            if (!inputValue) {
              return item;
            }
            const prepareSearch = inputValue.replace(/\s/g, '').toLowerCase();

            if (!prepareSearch.split(',').filter(Boolean).every(isNaN)) {
              return prepareSearch
                .split(',')
                .filter(Boolean)
                .filter((e) => !isNaN(e))
                .some((value) => String(item.id).replace(/\s/g, '').toLowerCase().includes(value));
            }

            return (item.title + item.id).replace(/\s/g, '').toLowerCase().includes(prepareSearch);
          });

          return [
            ...acc,
            filteredServices.length > 0 && {
              title: item.title || item.label,
              isGroupHeader: true,
            },
            ...filteredServices,
          ];
        }, [])
        ?.filter(Boolean);
    }
    return options?.filter((item) => {
      if (!inputValue) {
        return item;
      }
      const prepareSearch = inputValue.replace(/\s/g, '').toLowerCase();
      if (!prepareSearch.split(',').filter(Boolean).every(isNaN)) {
        return prepareSearch
          .split(',')
          .filter(Boolean)
          .filter((e) => !isNaN(e))
          .some((value) => String(item.id).replace(/\s/g, '').toLowerCase().includes(value));
      }
      return (item.title + item.id).replace(/\s/g, '').toLowerCase().includes(prepareSearch);
    });
  }, [grouped, options, inputValue]);

  const onEnter = (index) => {
    const finded = filteredOptions.find((_, idx) => idx === index);
    if (finded) {
      setSelected(finded);
      onSelect?.(finded);
      onToggleClose();
    }
  };

  const [focusedIndex, setCurrentFocus] = useKeyboard({
    open,
    onEnter,
    listSize: filteredOptions?.length,
    options: filteredOptions,
    initialFocus: preselectFirst ? 0 : -1,
  });

  const onSelectValue = (item, index) => {
    setCurrentFocus(index);
    setSelected(item);
    onSelect?.(item);
  };

  const listRefs = filteredOptions.reduce((acc, _, index) => {
    acc[index] = createRef();
    return acc;
  }, {});

  const focusOnActiveItem = useCallback(
    (index, behavior = 'smooth') => listRefs?.[index]?.current?.scrollIntoView({ block: 'center', behavior }),
    [listRefs],
  );

  useEffect(() => {
    if (inputValue !== '') {
      return grouped ? setCurrentFocus(1) : setCurrentFocus(0);
    }
    if (selected) {
      const findIndex = filteredOptions.findIndex((item) => item.id === selected.id);
      findIndex && setCurrentFocus(findIndex);
    } else {
      setCurrentFocus(-1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);

  useEffect(() => {
    if (focusedIndex >= 0) {
      focusOnActiveItem(focusedIndex, focusedIndex === 0 ? 'auto' : 'smooth');
    }
  }, [focusOnActiveItem, focusedIndex]);

  useEffect(() => {
    if (open) {
      return inputRef?.current?.focus();
    }
    if (!open) {
      if (!selected) setCurrentFocus(-1);
      if (selected) {
        const findIndex = filteredOptions.findIndex((item) => item.id === selected.id);
        if (findIndex) {
          setCurrentFocus(findIndex);
          focusOnActiveItem(findIndex, 'auto');
        }
      }
      setInputValue('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selected]);

  useEffect(() => {
    if (toggleText && !selected) {
      setToggle(toggleText);
    } else {
      setToggle(selected.title);
    }
  }, [toggleText, selected]);

  useEffect(() => {
    if (preselectFirst) {
      if (grouped && options) {
        const firstOpt = options?.[0]?.options?.[0];
        onSelect?.(firstOpt);
        setSelected(firstOpt);
      } else {
        setSelected(options?.[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectFirst, options]);

  return (
    <>
      {label && <ControlLabel>{label}</ControlLabel>}
      <Dropdown
        open={open}
        onFocus={() => handleFocus()}
        className={classNames('dropdown-search-select', className, {
          'not-selected': !selected,
          grouped,
        })}
        onToggle={onToggleClose}
        id="dropdown"
        rootCloseEvent="click"
      >
        <Toggle bsRole="toggle">{toggle}</Toggle>

        <Menu
          bsRole="menu"
          inputRef={inputRef}
          listRefs={listRefs}
          filteredOptions={filteredOptions}
          inputValue={inputValue}
          selected={selected}
          toggleText={toggleText}
          preselectFirst={preselectFirst}
          focusedIndex={focusedIndex}
          grouped={grouped}
          showOptionId={showOptionId}
          listMaxHeight={listMaxHeight}
          onSelectValue={onSelectValue}
          setInputValue={setInputValue}
          handleFocus={handleFocus}
          onToggleClose={onToggleClose}
        />
      </Dropdown>
    </>
  );
};

SearchFilter.propTypes = {
  options: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  className: PropTypes.string,
  label: PropTypes.string,
  toggleText: PropTypes.string,
  preselectFirst: PropTypes.bool,
  grouped: PropTypes.bool,
  showOptionId: PropTypes.bool,
  listMaxHeight: PropTypes.number,
  onSelect: PropTypes.func,
};

SearchFilter.defaultProps = {
  options: [],
  toggleText: 'Select',
  showOptionId: false,
  listMaxHeight: 260,
};

export default SearchFilter;
